'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps } from 'react';
import { TruncatedTitle } from '@/lib/components/Text/TruncatedTitle';
import { Link } from '@/lib/routing';
import { Card } from '@/lib/ui-kit/card';
import { Icon } from '@/lib/ui-kit/icons';
import { Text } from '@/lib/ui-kit/text';
import { UiImage } from '@/lib/ui-kit/ui-image';
import { cn } from '@/lib/ui-kit/utils';
import { formatAmount } from '@/lib/utils/formatAmount';

export interface EventLineCardData {
  id: string;
  slug: string;
  title: string;
  iconUrl: string;
  endDate?: string;
  endTradeDate?: string;
  volume: number;
  topOutcome: {
    name: string;
    probability: number;
  };
}

interface EventLineCardProps extends ComponentProps<'div'> {
  event?: EventLineCardData;
  eventId?: string;
  slug?: string;
  /**
   * When set, the card links via a plain `<a href>` instead of the in-zone
   * next-intl `<Link>`. Required when rendering from another zone (e.g. news),
   * where soft-routing to `/event/*` would break. Pass a locale-prefixed href.
   */
  hrefOverride?: string;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const months = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

export function CircularProgress({
  percentage,
  size = 64,
  strokeWidth = 3,
  className,
}: CircularProgressProps) {
  const clamped = Math.max(0, Math.min(percentage, 100));
  const width = size;
  const height = Math.round(size / 2 + strokeWidth);
  const radius = (size - strokeWidth) / 2;
  const startX = strokeWidth / 2;
  const startY = height - strokeWidth / 2;
  const endX = width - strokeWidth / 2;
  const endY = startY;
  // Semicircle arc path from left to right (top arc)
  const d = `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width, height }}
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Track */}
        <path
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-[#C0C3CE]"
          strokeLinecap="round"
          pathLength={100}
        />
        {/* Progress */}
        {clamped > 0 && (
          <path
            d={d}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-[#FF6B35] transition-all duration-300 ease-in-out"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={100}
            strokeDashoffset={100 - clamped}
          />
        )}
      </svg>
      {/* Percentage text */}
      <div className="absolute inset-0 top-5 flex items-center justify-center">
        <Text variant="body-small" className="font-semibold text-txt-1">
          {clamped}%
        </Text>
      </div>
    </div>
  );
}

export function EventLineCard({
  event: eventProp,
  eventId: _eventId,
  slug,
  hrefOverride,
  className,
  ...props
}: EventLineCardProps) {
  const t = useTranslations('event.card');
  // Use provided event data or fallback to mock
  const event = eventProp;
  if (!event) {
    return null;
  }
  const eventSlug = slug || event.slug;
  const eventLink = `/event/${eventSlug}`;

  return (
    <Card
      className={cn(
        `relative flex w-full cursor-pointer flex-row items-center justify-between gap-3 rounded-[10px]
        border border-[#2A3032] bg-transparent px-3 py-2 shadow-none`,
        className,
      )}
      {...props}
    >
      {hrefOverride ? (
        <a
          href={hrefOverride}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute inset-0 z-[1]"
          aria-label={event.title}
        />
      ) : (
        <Link
          href={eventLink}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute inset-0 z-[1]"
          aria-label={event.title}
        />
      )}

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="size-11 shrink-0 overflow-hidden rounded-[8px]">
          <UiImage
            size={44}
            src={event.iconUrl}
            fallback="/evfallback.webp"
            alt={event.title}
            className="size-full object-cover"
          />
        </div>

        <div className="flex min-w-0 flex-col justify-center gap-1">
          <TruncatedTitle className="line-clamp-2 text-[15px] font-semibold leading-[19px] text-white">
            {event.title}
          </TruncatedTitle>
          <div className="flex flex-wrap items-center gap-4 text-[#74797F]">
            {(event.endTradeDate || event.endDate) && (
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={16} />
                <Text variant="label-md">
                  {t('ends')} {formatDate(event.endTradeDate ?? event.endDate!)}
                </Text>
              </div>
            )}
            <Text variant="label-md">{formatAmount(event.volume).toUpperCase()} Vol.</Text>
          </div>
        </div>
      </div>

      <div className="flex w-[68px] shrink-0 flex-col items-center">
        <CircularProgress percentage={event.topOutcome.probability} size={64} strokeWidth={3} />
        <Text variant="label-md" className="w-11 truncate text-center text-[#74797F]">
          {event.topOutcome.name}
        </Text>
      </div>
    </Card>
  );
}
